import { createElement, useContext } from 'react';
import { TableColumn, ValueType } from './models';
import { ViewRenderer } from '../DashboardViewer';
import { TemplateRenderer } from '../DashboardViewer';
import { DashboardContext } from '../../../hooks';
import { isTemplate } from '../../../utils';
import { getCellRendererProps } from './DataGridUtils/getCellRendererProps';
import { formatValue } from '.';
import { CellContext, Row, Table } from '@tanstack/react-table';
import { get } from 'lodash';

export const CellRenderer = (context: CellContext<unknown, never>): JSX.Element => {
  const { column, getValue, row, table } = context;
  const { ComponentRegistry, TemplateRegistry } = useContext(DashboardContext);

  const value = getValue();

  // handle cell templates
  const cellTemplate = column?.columnDef?.meta?.cellTemplate;
  if (cellTemplate) {
    const props = getCellRendererProps(
      cellTemplate.type,
      row as unknown as Row<TableColumn>,
      value,
      table as unknown as Table<TableColumn>,
      cellTemplate.props,
    );

    if (isTemplate(TemplateRegistry, cellTemplate.name)) {
      return <TemplateRenderer name={cellTemplate.name} props={props} />;
    }

    return <ViewRenderer view={[[cellTemplate.name, props]]} name={`i${cellTemplate.name}`} />;
  }

  // handle React component as cell renderer
  const cellRenderer = column?.columnDef?.meta?.cellRenderer;
  if (cellRenderer) {
    const props = {
      ...getCellRendererProps(
        cellRenderer.type,
        row as unknown as Row<TableColumn>,
        value,
        table as unknown as Table<TableColumn>,
        cellRenderer.props,
      ),
      ...{
        key: `${cellRenderer.name}-dashify:DataGrid-row-${row.id}`,
      },
    };
    // directly use individual cell renderer React component for better performance
    const Component = get(ComponentRegistry, cellRenderer.name);
    if (Component) {
      return createElement(Component, props);
    }
    return <ViewRenderer view={[[cellRenderer.name, props]]} name={`i${cellRenderer.name}`} />;
  }

  // apply formatting if applicable
  const valueType = column?.columnDef?.meta?.valueType || ValueType.STRING;
  const displayValue =
    value != null
      ? column?.columnDef?.meta?.formatData
        ? formatValue(value, valueType, column?.columnDef?.meta?.dataFormatter)
        : value
      : '—';

  return <>{displayValue}</>;
};
